import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, PlayCircle, ShoppingCart, CheckCircle } from 'lucide-react';
import { StandaloneUnitDto } from '../../types/unit';
import { cardTheme } from '../../styles/cardTheme';
import { getStandaloneUnitImageUrl } from '../../services/unitBrowseService';

interface StandaloneUnitCardProps {
  unit: StandaloneUnitDto;
  isEnrolled?: boolean;
}

const StandaloneUnitCard: React.FC<StandaloneUnitCardProps> = ({
  unit,
  isEnrolled = false,
}) => {
  const navigate = useNavigate();

  const isFree = unit.price === 0;
  const formattedPrice = unit.price ? unit.price.toLocaleString('ar-EG') : '0';
  const displayTitle = unit.titel || unit.unitTitle || unit.unitName;

  const goToDetails = () => {
    navigate(`/units/${unit.id}`, { state: { unit } });
  };

  const handleSubscribe = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isEnrolled) {
      navigate(`/unit-viewer/${unit.id}`);
      return;
    }
    goToDetails();
  };

  return (
    <article
      className={`group ${cardTheme.courseCard} flex flex-col min-w-0 cursor-pointer`}
      onClick={goToDetails}
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-green-50 shrink-0">
        <img
          src={getStandaloneUnitImageUrl(unit)}
          alt={displayTitle}
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
          onError={(e) => {
            e.currentTarget.src = '/default-course.jpg';
          }}
        />
        {isFree && !isEnrolled && (
          <span className="absolute top-3 right-3 z-10 bg-gold-500 text-green-950 text-xs font-bold px-3 py-1 rounded-full">
            مجاني
          </span>
        )}
        {isEnrolled && (
          <span className="absolute top-3 right-3 z-10 bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            مشترك
          </span>
        )}
      </div>

      <div className="flex flex-col flex-1 p-4 sm:p-5 border-t-4 border-gold-500">
        <p className="text-xs text-gold-700 font-semibold mb-1">حصة مستقلة</p>
        <h3 className={`text-base sm:text-lg font-bold mb-1 line-clamp-2 ${cardTheme.title}`}>
          {displayTitle}
        </h3>
        {unit.unitName && unit.unitName !== displayTitle && (
          <p className="text-sm text-gray-500 line-clamp-1 mb-3">{unit.unitName}</p>
        )}

        <div className="mt-auto flex flex-col gap-3 pt-3 border-t border-green-100">
          <div>
            {isEnrolled ? (
              <span className={`text-sm font-bold ${cardTheme.price}`}>مشترك بالفعل</span>
            ) : isFree ? (
              <span className={`text-lg font-bold ${cardTheme.price}`}>مجاناً</span>
            ) : (
              <div className="flex items-baseline gap-1">
                <span className="text-lg font-bold text-green-800">{formattedPrice}</span>
                <span className="text-sm font-semibold text-gold-600">جنيه</span>
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <Link
              to={`/units/${unit.id}`}
              state={{ unit }}
              onClick={(e) => e.stopPropagation()}
              className={`inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold min-h-11 ${cardTheme.btnOutline}`}
            >
              <BookOpen className="w-4 h-4" />
              التفاصيل
            </Link>
            <button
              type="button"
              onClick={handleSubscribe}
              className={`inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold min-h-11 ${cardTheme.btnPrimary}`}
            >
              {isEnrolled ? (
                <>
                  <PlayCircle className="w-4 h-4" />
                  ادخل الحصة
                </>
              ) : (
                <>
                  <ShoppingCart className="w-4 h-4" />
                  {isFree ? 'اشترك مجاناً' : 'اشترك الآن'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </article>
  );
};

export default StandaloneUnitCard;
